import { IconFont, Text, Tooltip } from '@byecode/ui'
import { Button, FileButton, Menu, Popover, TextInput } from '@mantine/core'
import { type Editor, useCurrentEditor } from '@tiptap/react'
import classNames from 'classnames'
import React, { forwardRef, useCallback, useRef, useState } from 'react'

import { useLanguageContext } from '../../../contexts'
import { DropdownInput } from './DropdownInput'
import { CustomActionIcon } from './styles'

interface LinkButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
    editor: Editor | null
    onToggleLink: () => void
}
const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(({ editor, className, onClick, onToggleLink, ...props }, ref) => {
    const { convertTextByLanguage } = useLanguageContext()
    return (
        <Tooltip
            title={
                <div style={{ textAlign: 'center' }}>
                    <Text>{convertTextByLanguage('hyperlink')}</Text>
                    <Text color="var(--color-gray-400)">⌘+K</Text>
                </div>
            }
        >
            <CustomActionIcon
                ref={ref}
                className={classNames({ active: editor?.isActive('link'), className })}
                {...props}
                onClick={e => {
                    onClick?.(e)
                    onToggleLink()
                }}
            >
                <IconFont type="LinkSimple" />
            </CustomActionIcon>
        </Tooltip>
    )
})

export const LinkMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()
    const [linkOpened, setLinkOpened] = useState(false)

    // 切换link
    const onToggleLink = useCallback(() => {
        const isLink = editor?.isActive('link')
        if (isLink) {
            return editor?.chain().focus().unsetLink().run()
        }
        setLinkOpened(s => !s)
    }, [editor])

    // 更新link地址
    const handleUpdateLink = (url: string) => {
        editor?.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run()
        setLinkOpened(false)
    }

    return (
        <Popover
            withinPortal
            trapFocus
            opened={linkOpened}
            onClose={() => setLinkOpened(false)}
            position="bottom"
            shadow="md"
            styles={{ dropdown: { padding: 12, borderRadius: 8 } }}
        >
            <Popover.Target>
                <LinkButton editor={editor} onToggleLink={onToggleLink} />
            </Popover.Target>
            <Popover.Dropdown>
                <DropdownInput
                    onConfirm={handleUpdateLink}
                    placeholder={convertTextByLanguage('pasteLink')}
                    confirmText={convertTextByLanguage('sure')}
                />
            </Popover.Dropdown>
        </Popover>
    )
}

interface LatexButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
    editor: Editor | null
}
const LatexButton = forwardRef<HTMLButtonElement, LatexButtonProps>(({ editor, ...props }, ref) => {
    return (
        <Tooltip title="Latex数学公式">
            <CustomActionIcon ref={ref} {...props}>
                <IconFont type="Latex" />
            </CustomActionIcon>
        </Tooltip>
    )
})
export const LatexMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()
    const [opened, setOpened] = useState(false)

    const handleInsertLatex = (str: string) => {
        editor?.commands.insertInlineMath({
            latex: `${str}`
        })
        setOpened(false)
    }

    return (
        <Popover
            trapFocus
            opened={opened}
            onClose={() => setOpened(false)}
            position="bottom"
            shadow="md"
            styles={{ dropdown: { padding: 12, borderRadius: 8 } }}
        >
            <Popover.Target>
                <LatexButton editor={editor} onClick={() => setOpened(true)} />
            </Popover.Target>
            <Popover.Dropdown>
                <DropdownInput onConfirm={handleInsertLatex} confirmText={convertTextByLanguage('sure')} />
            </Popover.Dropdown>
        </Popover>
    )
}

export const CodeMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()

    return (
        <Tooltip
            title={
                <div style={{ textAlign: 'center' }}>
                    <Text>{convertTextByLanguage('insertCode')}</Text>
                    <Text color="var(--color-gray-400)">⌘+Option+C</Text>
                </div>
            }
        >
            <CustomActionIcon
                className={classNames({ active: editor?.isActive('codeBlock') })}
                onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
            >
                <IconFont type="TextCodeInline" />
            </CustomActionIcon>
        </Tooltip>
    )
}

export const ImageMenu = () => {
    const { editor } = useCurrentEditor()
    const ref = useRef<() => void>(() => void 0)
    const { convertTextByLanguage } = useLanguageContext()

    const onChange = useCallback(
        (payload: File[]) => {
            editor?.commands.insertImg(payload)
            // 上传完后清除input
            ref.current()
        },
        [editor?.commands]
    )

    return (
        <FileButton resetRef={ref} onChange={onChange} accept="image/*" multiple>
            {props => (
                <Tooltip title={convertTextByLanguage('uploadImage')}>
                    <CustomActionIcon {...props} variant="subtle">
                        <IconFont type="BlockImage" />
                    </CustomActionIcon>
                </Tooltip>
            )}
        </FileButton>
    )
}

export const VideoMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [menuOpened, setMenuOpened] = useState(false)
    const [linkPopoverOpened, setLinkPopoverOpened] = useState(false)
    const [videoUrl, setVideoUrl] = useState('')

    // 处理本地上传
    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (file) {
                editor?.commands.insertVideo([file])
            }
            // 重置 input 以便可以再次选择相同文件
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        },
        [editor?.commands]
    )

    // 点击本地上传
    const handleLocalUploadClick = useCallback(() => {
        setMenuOpened(false)
        // 延迟触发文件选择，确保 Menu 已关闭
        setTimeout(() => {
            fileInputRef.current?.click()
        }, 0)
    }, [])

    // 处理视频链接
    const handleInsertVideoByUrl = useCallback(() => {
        if (videoUrl) {
            editor?.commands.insertVideoByUrl(videoUrl)
            setVideoUrl('')
            setLinkPopoverOpened(false)
        }
    }, [editor?.commands, videoUrl])

    // 点击视频链接选项
    const handleVideoLinkClick = useCallback(() => {
        setMenuOpened(false)
        setLinkPopoverOpened(true)
    }, [])

    return (
        <Popover
            withinPortal
            trapFocus
            opened={linkPopoverOpened}
            onClose={() => setLinkPopoverOpened(false)}
            position="bottom"
            shadow="md"
            styles={{ dropdown: { padding: 12, borderRadius: 8 } }}
        >
            <Popover.Target>
                <span style={{ display: 'inline-flex' }}>
                    <Menu opened={menuOpened} onChange={setMenuOpened} width={174} position="bottom" withinPortal>
                        <Menu.Target>
                            <Tooltip title={convertTextByLanguage('insertVideo')}>
                                <CustomActionIcon variant="subtle" onClick={() => setMenuOpened(o => !o)}>
                                    <IconFont type="BlockVideo" />
                                </CustomActionIcon>
                            </Tooltip>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item onClick={handleVideoLinkClick}>{convertTextByLanguage('videoLink')}</Menu.Item>
                            <Menu.Item onClick={handleLocalUploadClick}>{convertTextByLanguage('localUpload')}</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                    <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileChange} style={{ display: 'none' }} />
                </span>
            </Popover.Target>
            <Popover.Dropdown>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <TextInput
                        value={videoUrl}
                        onChange={e => setVideoUrl(e.target.value)}
                        placeholder={convertTextByLanguage('pasteVideoLink')}
                    />
                    <Button
                        fullWidth
                        disabled={!videoUrl}
                        onClick={handleInsertVideoByUrl}
                        styles={{ root: { backgroundColor: '#101828', '&:hover': { backgroundColor: '#1d2939' } } }}
                    >
                        {convertTextByLanguage('sure')}
                    </Button>
                </div>
            </Popover.Dropdown>
        </Popover>
    )
}

export const QuoteMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()
    return (
        <Tooltip
            title={
                <div style={{ textAlign: 'center' }}>
                    <Text>{convertTextByLanguage('insertCitation')}</Text>
                    <Text color="var(--color-gray-400)">⌘+Shift+U</Text>
                </div>
            }
        >
            <CustomActionIcon
                className={classNames({ active: editor?.isActive('blockquote') })}
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            >
                <IconFont type="Quote" />
            </CustomActionIcon>
        </Tooltip>
    )
}

export const LineMenu = () => {
    const { editor } = useCurrentEditor()
    const { convertTextByLanguage } = useLanguageContext()
    return (
        <Tooltip
            title={
                <div style={{ textAlign: 'center' }}>
                    <Text>{convertTextByLanguage('divider')}</Text>
                    <Text color="var(--color-gray-400)">---</Text>
                </div>
            }
        >
            <CustomActionIcon variant="subtle" onClick={() => editor?.chain().focus().setHorizontalRule().run()}>
                <IconFont type="BlockLine" />
            </CustomActionIcon>
        </Tooltip>
    )
}
